// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import L from 'leaflet';
import myw from 'myWorld-base';
import config from 'myWorld/base/config';
import './mapControl';

myw.GeoMapControl = myw.MapControl.extend({
    messageGroup: 'MapControl', //necessary because Leaflet's __super__ is broken in 1.0.3

    options: {
        maxZoom: config['core.map.maxZoom'],
        minZoom: 1,
        inertia: config['core.map.panInertia'] || false //default to false to avoid out-of-sync effect with google maps
    },

    /**
     * @class  Map Control for the geographical world.
     *         Extends myw.MapControl adding functionality to load, manage and handle layers
     * @param  {Application} app        The current myWorld application instance
     * @param {string|domElement} divID     The div element (or it's id) to render the map onto.
     * @constructs
     * @extends {MapControl}
     */
    initialize(app, divID) {
        const worldId = 'geo';

        myw.MapControl.prototype.initialize.call(this, app, divID, worldId);

        /** Leaflet basemap control @type {L.Control.Layers} */
        this.basemapControl = null;

        this.app.on('internetStatus-changed', e => {
            if (e.hasInternetAccess && e.hadInternetAccess !== undefined) {
                //refresh current basemap which may not be displaying properly as internet was not available
                const basemap = this.getCurrentBaseMap();
                if (basemap && basemap.redraw) basemap.redraw();
            }
        });

        //wait for the map to be ready before adding layers, otherwise somethings like map bounds may not be
        //ready when initializing layers (which is relevant for myworld feature layers)
        this.whenReady(() => {
            //initialize the available base maps. Needs to happen after Map is "ready" because that's when the initialBaseMapName is also set
            this.initializeBaseMapsFromDb();

            // loads the layers for the current user
            this.layerManager.ensureInitialLayers();
        });
    },

    /**
     * Obtains the available background maps and sets the initial one
     *
     * The initial background map is obtained from the 'home' bookmark
     */
    async initializeBaseMapsFromDb() {
        const layersDefs = await this.app.getLayersDefs();
        let defs = _.where(layersDefs, { category: 'basemap' });
        defs = _.sortBy(defs, 'name'); // Alphabetically sort the base layers
        this.baseLayerDefs = defs;

        _.each(defs, this.addBaseLayer, this);

        //set the initial base map
        const firstBaseMap = defs[0];
        //If there is an initialBaseMapName setting and it exists in the currently available basemaps, set that
        if (
            this.initialBaseMapName &&
            _.findWhere(defs, { display_name: this.initialBaseMapName })
        ) {
            this.setCurrentBaseMap(this.initialBaseMapName);
        } else if (firstBaseMap) {
            //Otherwise set the first basemap in the list of available basemaps as the current basemap
            this.setCurrentBaseMap(firstBaseMap.name);
        }

        //If there are more than 1 basemaps added to the application,
        //add the Leaflet control that allows the user to choose the base map
        //need to be done after we have the layers
        if (_.keys(this.baseMaps).length > 1) this.addBaseMapControl(this.baseMaps);
    },

    /**
     * Tries to create a leaflet layer from a definition and if successful adds it to the list of available basemaps
     * @param {layerDefinition} def     Definition of the layer to add
     * @return {promise} Promise for the created leaflet layer or null if it was not possible to create it
     */
    addBaseLayer(def) {
        def.options = {
            zIndex: -1, //so it goes behind overlays
            maxZoom: def.max_scale,
            minZoom: def.min_scale,
            attribution: def.attribution,
            opacity: 1.0 - def.transparency / 100
        };

        const datasource = this.app.database.getDatasource(def.datasource);
        if (!datasource) {
            console.log(`***Warning: No datasource for layer: ${def.name}`);
            return;
        }
        const layer = new myw.Layer(datasource, def);

        //store the name directly in the layer object so we can use it when handling
        //basemap changes, as the layer definition will not be available
        layer.display_name = def.display_name;
        this.baseMaps[def.display_name] = layer;

        if (this.basemapControl) {
            //the control has already been created at this point,
            //so we need to add the new basemap to it
            this.basemapControl.addBaseLayer(layer, layer.display_name);
        }
    },

    /**
     * Adds the Leaflet control that allows the user to choose the base map
     * Adds some custom styles to the control
     */
    addBaseMapControl(backgroundLayers) {
        // utilize leaflet's base map control
        this.addControl(
            (this.basemapControl = new L.Control.Layers(
                backgroundLayers,
                {},
                {
                    autoZIndex: false //needed to set this due to funky draw ordering
                }
            ))
        );
        const baseLayersList = $(this.basemapControl._baseLayersList),
            baseLayerLabels = baseLayersList.children();

        // Add a selected class to the current basemap
        baseLayersList
            .find('input:checked')
            .parent('label')
            .addClass('selected');

        //Add a 'click' event handler to move the selected class to the newly selected basemap in the list
        baseLayerLabels.on('click', function() {
            baseLayersList.find('.selected').removeClass('selected');
            $(this).addClass('selected');
        });
    },

    /**
     * Reconfigures the map with the information saved in the boomark
     * @param  {Object} bookmarkDetails [description]
     */
    useBookmark(bookmarkDetails) {
        //set the map view
        this.setView(new L.LatLng(bookmarkDetails.lat, bookmarkDetails.lng), bookmarkDetails.zoom);

        if (bookmarkDetails.map_display) {
            // Get the map information from the bookmark
            const mapProperties = bookmarkDetails.map_display.split('|'),
                baseMapName = mapProperties[0];

            // Update the basemap.
            this.setCurrentBaseMap(baseMapName);

            // Update the overlays.
            if (mapProperties[1]) {
                this.layerManager.setLayersVisibility(mapProperties[1].split(','));
            }
        }
    },

    /**
     * Refreshes the myworld_vector layers
     */
    update() {
        this.layerManager.refreshMywVectorLayers();
    }
});

export default myw.GeoMapControl;
